import React from 'react';
import { Mail, Phone, MapPin, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { companyInfo } from '../../mock';
import { useContactForm } from '../../hooks/useContactForm';

const ContactInfo = () => (
  <div className="space-y-8">
    <div className="flex items-start space-x-4">
      <Mail className="h-6 w-6 text-amber-500 mt-1" />
      <div>
        <h3 className="font-semibold text-lg mb-1">Email</h3>
        <p className="text-gray-400">{companyInfo.email}</p>
      </div>
    </div>
    <div className="flex items-start space-x-4">
      <Phone className="h-6 w-6 text-amber-500 mt-1" />
      <div>
        <h3 className="font-semibold text-lg mb-1">Customer Support No</h3>
        <p className="text-gray-400">+91-8953802576</p>
      </div>
    </div>
    <div className="flex items-start space-x-4">
      <MapPin className="h-6 w-6 text-amber-500 mt-1" />
      <div className="w-full">
        <h3 className="font-semibold text-lg mb-1">Address</h3>
        <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
          <p className="text-amber-500 font-bold text-base mb-2 uppercase tracking-wide">
            National Corporate Communication Address
          </p>
          <p className="text-white font-medium leading-relaxed">
            E-132 Basement,<br />
            East of Kailash,<br />
            New Delhi - 110065
          </p>
        </div>
        <div className="mt-3">
          <p className="text-gray-600 text-xs leading-relaxed">
            State Headquarter (Uttar Pradesh)<br />
            32/85, Maniram Bagia, Kanpur- 208012
          </p>
        </div>
      </div>
    </div>
  </div>
);

const SuccessState = () => (
  <div className="text-center py-12" data-testid="contact-form-success">
    <CheckCircle2 className="h-16 w-16 text-amber-500 mx-auto mb-4" />
    <h3 className="text-xl font-semibold mb-2">Message Sent!</h3>
    <p className="text-gray-400">We'll get back to you soon.</p>
  </div>
);

const ContactForm = () => {
  const {
    formData,
    formSubmitted,
    formError,
    submitting,
    handleInputChange,
    handleSubmit,
  } = useContactForm();

  if (formSubmitted) return <SuccessState />;

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
      <div>
        <label htmlFor="name" className="block text-sm font-medium mb-2">Name</label>
        <Input
          id="name"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          className="bg-black border-gray-700 focus:border-amber-500 text-white"
          placeholder="Your name"
          required
          data-testid="contact-form-name-input"
        />
      </div>
      <div>
        <label htmlFor="email" className="block text-sm font-medium mb-2">Email</label>
        <Input
          id="email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleInputChange}
          className="bg-black border-gray-700 focus:border-amber-500 text-white"
          placeholder="your@email.com"
          required
          data-testid="contact-form-email-input"
        />
      </div>
      <div>
        <label htmlFor="phone" className="block text-sm font-medium mb-2">
          Phone <span className="text-gray-500 text-xs">(optional)</span>
        </label>
        <Input
          id="phone"
          name="phone"
          type="tel"
          value={formData.phone}
          onChange={handleInputChange}
          className="bg-black border-gray-700 focus:border-amber-500 text-white"
          placeholder="+91-XXXXXXXXXX"
          data-testid="contact-form-phone-input"
        />
      </div>
      <div>
        <label htmlFor="message" className="block text-sm font-medium mb-2">Message</label>
        <Textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleInputChange}
          className="bg-black border-gray-700 focus:border-amber-500 text-white min-h-[120px]"
          placeholder="How can we help you?"
          required
          data-testid="contact-form-message-input"
        />
      </div>
      {formError && (
        <p className="text-red-400 text-sm" data-testid="contact-form-error">
          {formError}
        </p>
      )}
      <Button
        type="submit"
        disabled={submitting}
        className="w-full bg-amber-500 hover:bg-amber-600 text-black font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        data-testid="contact-form-submit-button"
      >
        {submitting ? 'Sending...' : 'Send Message'}
      </Button>
    </form>
  );
};

export const ContactSection = () => (
  <section id="contact" className="py-20 px-4 bg-black" data-testid="contact-section">
    <div className="container mx-auto">
      <h2 className="text-4xl md:text-5xl font-bold mb-4 text-center">
        Get In <span className="text-amber-500">Touch</span>
      </h2>
      <p className="text-center text-gray-400 mb-12 max-w-2xl mx-auto">
        Have questions about our products? We're here to help you find the right electrical solutions
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
        <ContactInfo />
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-8" data-testid="contact-form-container">
          <ContactForm />
        </div>
      </div>
    </div>
  </section>
);
